import React, { createContext, memo } from 'react'
import { useCallback, useContext, useMemo } from 'sb-hooks'
import { getRandomId } from 'common/helpers'

import { AccordionContext } from './Accordion'


type AccordionItemState = {
  buttonId: string
  panelId: string
  isOpen: boolean
}

type AccordionItemContextValue = {
  state: AccordionItemState
  setOpen: (isOpen: boolean) => void
}

export const AccordionItemContext = createContext<AccordionItemContextValue>(null)

type AccordionItemProps = {
  id?: string
  children: ((params: { isOpen: boolean, activeItem?: string }) => React.ReactNode) | React.ReactNode
}

const AccordionItem: React.FunctionComponent<AccordionItemProps> = (props) => {
  const { children, id } = props

  const { activeItem, setActiveItem } = useContext(AccordionContext)

  const ids = useMemo(() => ({
    buttonId: id || getRandomId('button'),
    panelId: getRandomId('panel'),
  }), [])

  const state = useMemo<AccordionItemState>(() => ({
    ...ids,
    isOpen: activeItem === ids.buttonId,
  }), [ activeItem, ids ])

  const handleButtonClick = useCallback((isOpen?: boolean) => {
    setActiveItem(isOpen ? ids.buttonId : null)
  }, [ ids, setActiveItem ])

  const itemContextValue = useMemo(
    () => ({ state, setOpen: handleButtonClick }),
    [ handleButtonClick, state ]
  )

  return (
    <AccordionItemContext.Provider value={itemContextValue}>
      {
        typeof children === 'function' ? (
          // @ts-ignore because of ReactNode
          children({ isOpen: state.isOpen, activeItem })
        ) : (
          children
        )
      }
    </AccordionItemContext.Provider>
  )
}


export default memo(AccordionItem)
